import React, { useState } from 'react';
import { Check, Copy, RefreshCw } from 'lucide-react';
import JanelaModal from '../JanelaModal';
import { criarLaboratorio, salvarContrato, definirDono, cnpjDigitos, cnpjComMascara } from '../../data/admin';
import { callEdgeFunction } from '../../data/edge';
import { normalizarUsuario, DOMINIO_INTERNO } from '../../data/usuario';
import { MARK } from '../../constants';
import SebraeDoLaboratorio from './SebraeDoLaboratorio';

// Mesmo alfabeto do link do cliente, pela mesma razão: a senha vai ser lida em
// voz alta no telefone. Sem O/0, I/1, S/5.
const ALFABETO = 'ABCDEFGHJKLMNPQRTUVWXYZ23456789';
const senhaSugerida = () => {
  const n = new Uint32Array(8);
  (window.crypto || window.msCrypto).getRandomValues(n);
  return Array.from(n, (v) => ALFABETO[v % ALFABETO.length]).join('');
};

const TIERS = [
  { v: '',          r: 'Não definido' },
  { v: 'interno',   r: 'Interno (não fatura)' },
  { v: 'piloto',    r: 'Piloto' },
  { v: 'essencial', r: 'Essencial' },
  { v: 'pro',       r: 'Pro' },
];

const PASSOS = [
  'Criar o laboratório',
  'Salvar o contrato',
  'Criar o administrador',
  'Definir o dono',
];

const input = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Campo = ({ label, children, dica }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {dica && <p className="text-xs text-slate-400 mt-0.5">{dica}</p>}
  </div>
);

/**
 * Onboarding de um laboratório novo.
 *
 * É assistente e não formulário porque a ordem é obrigatória e uma falha no
 * meio deixa estado ruim: um laboratório criado sem o passo 3 é um laboratório
 * que existe e no qual NINGUÉM consegue entrar — `emitir-licenca` recusa quem
 * não é membro, e não há tela para o cliente se auto-adicionar.
 *
 * Os campos são pedidos todos de uma vez, e só a EXECUÇÃO é sequencial. Quatro
 * telas seguidas fariam quem já tem os dados na mão digitar em etapas sem
 * motivo; o que precisa ser visível é em que passo parou, não em que passo se
 * digita.
 *
 * Falha parcial NÃO desfaz sozinho: apagar laboratório é destrutivo e o erro
 * costuma ser rede. O assistente guarda o que já conseguiu e oferece "tentar
 * de novo" a partir dali.
 */
const NovoLaboratorio = ({ onPronto, onFechar }) => {
  const [f, setF] = useState({
    name: '', description: '',
    tier: 'piloto', razao_social: '', cnpj: '',
    contato_nome: '', contato_email: '', contato_telefone: '',
    sebrae: false, sebrae_responsavel: '',
    expires_at: '', mensalidade: '', franquia_sessoes_mes: '',
    preco_excedente_sessao: '', limite_membros: '', limite_clientes: '',
    admin_usuario: '', admin_nome: '', admin_senha: senhaSugerida(),
  });

  // Passo concluído por índice. Sobrevive a uma falha para o retry não repetir
  // o que já deu certo — e para não criar dois laboratórios com o mesmo nome.
  const [feitos, setFeitos]   = useState([]);
  const [lab, setLab]         = useState(null);
  const [rodando, setRodando] = useState(false);
  const [erro, setErro]       = useState('');
  const [pronto, setPronto]   = useState(null);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const usuario = normalizarUsuario(f.admin_usuario);
  const podeIr = f.name.trim() && usuario && f.admin_senha && !rodando;

  const vazioParaNulo = (v) => (v === '' || v == null ? null : v);
  const inteiro = (v) => (v === '' ? null : parseInt(v) || null);

  const executar = async () => {
    setRodando(true);
    setErro('');
    let labAtual = lab;

    try {
      // ── 1. O laboratório, sem dono ──
      if (!feitos.includes(0)) {
        labAtual = await criarLaboratorio({
          name: f.name.trim(),
          description: vazioParaNulo(f.description.trim()),
          sebrae: !!f.sebrae,
          // Responsável sem o programa não significa nada — não grava.
          sebrae_responsavel: f.sebrae ? vazioParaNulo(f.sebrae_responsavel.trim()) : null,
        });
        setLab(labAtual);
        setFeitos(p => [...p, 0]);
      }

      // ── 2. O contrato ──
      // A linha já existe: o trigger `trg_assina_laboratorio_novo` a criou no
      // insert acima, ativa. Aqui é UPDATE, nunca INSERT.
      if (!feitos.includes(1)) {
        await salvarContrato(labAtual.id, {
          tier:                   vazioParaNulo(f.tier),
          razao_social:           vazioParaNulo(f.razao_social.trim()),
          cnpj:                   cnpjDigitos(f.cnpj) || null,
          contato_nome:           vazioParaNulo(f.contato_nome.trim()),
          contato_email:          vazioParaNulo(f.contato_email.trim()),
          contato_telefone:       vazioParaNulo(f.contato_telefone.trim()),
          inicio_em:              new Date().toISOString().slice(0, 10),
          expires_at:             vazioParaNulo(f.expires_at),
          mensalidade:            f.mensalidade === '' ? null : Number(f.mensalidade),
          preco_excedente_sessao: f.preco_excedente_sessao === '' ? null : Number(f.preco_excedente_sessao),
          franquia_sessoes_mes:   inteiro(f.franquia_sessoes_mes),
          limite_membros:         inteiro(f.limite_membros),
          limite_clientes:        inteiro(f.limite_clientes),
        });
        setFeitos(p => [...p, 1]);
      }

      // ── 3. O primeiro administrador ──
      // Vai pela Edge Function porque criar conta exige service_role. Ela já
      // autoriza super admin explicitamente e insere em `lab_members`.
      let userId = pronto?.user_id;
      if (!feitos.includes(2)) {
        const { data: r } = await callEdgeFunction('admin-create-user', {
          username:      usuario,
          password:      f.admin_senha,
          full_name:     f.admin_nome.trim(),
          role:          'admin',
          laboratory_id: labAtual.id,
        }, { requireSession: true });

        if (!r?.success) throw new Error(r?.error || 'Falha ao criar o administrador');
        userId = r.data.user_id;
        setPronto({ user_id: userId, email: r.data.email, criado: r.data.created });
        setFeitos(p => [...p, 2]);
      }

      // ── 4. O dono ──
      // Só agora: daqui em diante `atualizar_membro` protege essa pessoa de
      // rebaixamento, e o laboratório se sustenta sozinho.
      if (!feitos.includes(3)) {
        await definirDono(labAtual.id, userId);
        setFeitos(p => [...p, 3]);
      }

      onPronto?.();
    } catch (e) {
      console.error('onboarding:', e);
      setErro(e.message || 'Falhou. O que já deu certo foi mantido.');
    } finally {
      setRodando(false);
    }
  };

  const terminou = feitos.length === PASSOS.length;
  const copiar = (t) => navigator.clipboard?.writeText(t);

  return (
    <JanelaModal chave="novo-laboratorio" titulo="Novo laboratório" onFechar={onFechar} largura="sm:max-w-3xl">

        {terminou ? (
          <div className="p-5 space-y-4">
            <p className="text-slate-900 font-medium">
              {MARK.OK} {f.name} está no ar.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <p className="text-xs font-medium text-slate-700">Entregue ao cliente</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500 w-16">Usuário</span>
                <code className="font-mono text-slate-900">{usuario}</code>
                <button onClick={() => copiar(usuario)} className="p-1 text-slate-400 hover:text-slate-700">
                  <Copy size={13} />
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500 w-16">Senha</span>
                <code className="font-mono text-slate-900">{f.admin_senha}</code>
                <button onClick={() => copiar(f.admin_senha)} className="p-1 text-slate-400 hover:text-slate-700">
                  <Copy size={13} />
                </button>
              </div>
              <p className="text-xs text-slate-500">
                O endereço interno é {usuario}@{DOMINIO_INTERNO} e nunca recebe mensagem —
                o cliente entra digitando só o usuário.
              </p>
            </div>

            {/* Honestidade sobre o que o software ainda não faz. Esconder isto
                vira uma ligação de suporte na primeira semana. */}
            <div className="text-sm text-slate-700 space-y-1">
              <p className="font-medium">Antes de entregar, saiba:</p>
              <p>
                {MARK.WARN} <strong>Não existe troca de senha no app.</strong> Esta senha vai
                ficar valendo até alguém do suporte trocá-la. Combine isso com o cliente.
              </p>
              <p>
                {MARK.PENDING} Peça que ele entre uma vez com internet — é o que emite a
                liberação do computador e permite trabalhar offline depois.
              </p>
            </div>

            <div className="flex justify-end">
              <button onClick={onFechar}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                Fechar
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Campo label="Nome do laboratório *">
                  <input className={input} value={f.name} disabled={feitos.includes(0)}
                    onChange={(e) => set('name', e.target.value)} />
                </Campo>
              </div>
              <Campo label="Plano">
                <select className={input} value={f.tier} onChange={(e) => set('tier', e.target.value)}>
                  {TIERS.map(t => <option key={t.v} value={t.v}>{t.r}</option>)}
                </select>
              </Campo>

              <div className="sm:col-span-2">
                <Campo label="Razão social">
                  <input className={input} value={f.razao_social}
                    onChange={(e) => set('razao_social', e.target.value)} />
                </Campo>
              </div>
              <Campo label="CNPJ">
                <input className={input} value={cnpjComMascara(f.cnpj)} placeholder="00.000.000/0000-00"
                  onChange={(e) => set('cnpj', cnpjDigitos(e.target.value))} />
              </Campo>

              <Campo label="Contato">
                <input className={input} value={f.contato_nome}
                  onChange={(e) => set('contato_nome', e.target.value)} />
              </Campo>
              <Campo label="E-mail">
                <input className={input} type="email" value={f.contato_email}
                  onChange={(e) => set('contato_email', e.target.value)} />
              </Campo>
              <Campo label="Telefone">
                <input className={input} value={f.contato_telefone}
                  onChange={(e) => set('contato_telefone', e.target.value)} />
              </Campo>

              <SebraeDoLaboratorio
                sebrae={f.sebrae} responsavel={f.sebrae_responsavel}
                onSebrae={(v) => set('sebrae', v)}
                onResponsavel={(v) => set('sebrae_responsavel', v)} />

              <Campo label="Vencimento" dica="Vazio = sem vencimento">
                <input className={input} type="date" value={f.expires_at}
                  onChange={(e) => set('expires_at', e.target.value)} />
              </Campo>
              <Campo label="Mensalidade (R$)">
                <input className={input} type="number" step="0.01" min="0" value={f.mensalidade}
                  onChange={(e) => set('mensalidade', e.target.value)} />
              </Campo>
              <Campo label="Franquia sessões/mês" dica="Acima disto é cobrado">
                <input className={input} type="number" min="1" value={f.franquia_sessoes_mes}
                  onChange={(e) => set('franquia_sessoes_mes', e.target.value)} />
              </Campo>

              <Campo label="Preço do excedente (R$)">
                <input className={input} type="number" step="0.01" min="0" value={f.preco_excedente_sessao}
                  onChange={(e) => set('preco_excedente_sessao', e.target.value)} />
              </Campo>
              <Campo label="Limite de membros" dica="Vazio = sem limite">
                <input className={input} type="number" min="1" value={f.limite_membros}
                  onChange={(e) => set('limite_membros', e.target.value)} />
              </Campo>
              <Campo label="Limite de clientes" dica="Vazio = sem limite">
                <input className={input} type="number" min="1" value={f.limite_clientes}
                  onChange={(e) => set('limite_clientes', e.target.value)} />
              </Campo>

              <div className="sm:col-span-3 border-t border-slate-200 pt-3 mt-1">
                <p className="text-xs font-medium text-slate-700 mb-2">
                  Primeiro administrador — sem ele ninguém entra no laboratório
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Usuário *"
                    dica={usuario ? `Entra digitando: ${usuario}` : 'Sem acento, sem espaço'}>
                    <input className={input} value={f.admin_usuario} disabled={feitos.includes(2)}
                      onChange={(e) => set('admin_usuario', e.target.value)} />
                  </Campo>
                  <Campo label="Nome completo">
                    <input className={input} value={f.admin_nome} disabled={feitos.includes(2)}
                      onChange={(e) => set('admin_nome', e.target.value)} />
                  </Campo>
                  <Campo label="Senha *">
                    <div className="flex gap-1">
                      <input className={input} value={f.admin_senha} disabled={feitos.includes(2)}
                        onChange={(e) => set('admin_senha', e.target.value)} />
                      {!feitos.includes(2) && (
                        <button onClick={() => set('admin_senha', senhaSugerida())}
                          title="Gerar outra" className="px-2 text-slate-400 hover:text-slate-700">
                          <RefreshCw size={14} />
                        </button>
                      )}
                    </div>
                  </Campo>
                </div>
              </div>
            </div>

            {(feitos.length > 0 || rodando) && (
              <div className="px-5 pb-3">
                <ol className="text-sm space-y-1">
                  {PASSOS.map((p, i) => (
                    <li key={p} className="flex items-center gap-2">
                      <span className="w-4 text-center">
                        {feitos.includes(i) ? <Check size={14} className="text-slate-900" />
                          : rodando && i === feitos.length ? '…' : ''}
                      </span>
                      <span className={feitos.includes(i) ? 'text-slate-500' : 'text-slate-700'}>{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {erro && (
              <div className="px-5 pb-3">
                <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
                  {erro}
                  {feitos.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Os passos concluídos foram mantidos. "Tentar de novo" continua de onde parou.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={onFechar}
                className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={executar} disabled={!podeIr}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
                {rodando ? 'Criando…' : feitos.length > 0 ? 'Tentar de novo' : 'Criar laboratório'}
              </button>
            </div>
          </>
        )}
    </JanelaModal>
  );
};

export default NovoLaboratorio;
